/**
 * cryptoStorage.ts
 *
 * Web Crypto API(AES-GCM 256)를 사용하는 암호화 스토리지 유틸리티.
 * Zustand persist 미들웨어의 PersistStorage<T> 타입과 완전히 호환됩니다.
 *
 * - 암호화 키는 PBKDF2로 파생 (브루트포스 방어)
 * - IV(초기화 벡터)는 매 암호화마다 랜덤 생성
 * - getItem/setItem에서 JSON 직렬화·역직렬화를 직접 처리
 * - 복호화 실패 시 자동 초기화 처리
 */

import { PersistStorage, StorageValue } from 'zustand/middleware';

// .env 파일에 VITE_STORAGE_SECRET=your-strong-random-secret 형태로 설정하세요.
const SECRET = import.meta.env.VITE_STORAGE_SECRET ?? 'ais-fallback-secret-key';
const SALT = 'ais-crypto-salt-v1';
const ITERATIONS = 100_000;

// ─── 내부 유틸 ────────────────────────────────────────────────────────────────

const enc = new TextEncoder();
const dec = new TextDecoder();

/** PBKDF2로 AES-GCM 256비트 키를 파생합니다 */
async function deriveKey(): Promise<CryptoKey> {
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        enc.encode(SECRET),
        { name: 'PBKDF2' },
        false,
        ['deriveKey'],
    );
    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: enc.encode(SALT),
            iterations: ITERATIONS,
            hash: 'SHA-256',
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    );
}

/** 평문 문자열을 AES-GCM으로 암호화 → Base64 문자열로 반환 */
async function encrypt(plaintext: string): Promise<string> {
    const key = await deriveKey();
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 96비트 IV
    const ciphertext = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        enc.encode(plaintext),
    );
    const combined = new Uint8Array(iv.byteLength + ciphertext.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(ciphertext), iv.byteLength);
    return btoa(String.fromCharCode(...combined));
}

/** Base64 암호문을 복호화 → 평문 문자열 반환 */
async function decrypt(base64: string): Promise<string> {
    const key = await deriveKey();
    const combined = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const plainBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        ciphertext,
    );
    return dec.decode(plainBuffer);
}

// ─── Zustand PersistStorage<T> 호환 팩토리 ───────────────────────────────────

/**
 * Zustand `persist` 미들웨어에 전달할 암호화 스토리지를 생성합니다.
 *
 * PersistStorage<unknown>을 사용하는 이유:
 * partialize 옵션이 저장 타입을 AuthState의 부분 집합으로 좁히기 때문에
 * 제네릭을 AuthState로 고정하면 Zustand 내부 타입과 불일치가 발생합니다.
 * unknown으로 완화하면 어떤 스토어/partialize 조합과도 호환됩니다.
 *
 * 사용 예:
 * ```ts
 * persist(storeCreator, {
 *   name: 'auth-storage',
 *   storage: createEncryptedStorage(),
 * })
 * ```
 */
export function createEncryptedStorage(): PersistStorage<unknown> {
    return {
        /** localStorage에서 읽어 복호화 + JSON.parse 후 반환 */
        getItem: async (
            name: string,
        ): Promise<StorageValue<unknown> | null> => {
            const raw = localStorage.getItem(name);
            if (!raw) return null;
            try {
                const decrypted = await decrypt(raw);
                return JSON.parse(decrypted) as StorageValue<unknown>;
            } catch {
                console.warn(
                    `[cryptoStorage] "${name}" 복호화 실패. 스토리지를 초기화합니다.`,
                );
                localStorage.removeItem(name);
                return null;
            }
        },

        /** JSON.stringify + 암호화 후 localStorage에 저장 */
        setItem: async (
            name: string,
            value: StorageValue<unknown>,
        ): Promise<void> => {
            const serialized = JSON.stringify(value);
            const encrypted = await encrypt(serialized);
            localStorage.setItem(name, encrypted);
        },

        /** localStorage에서 항목 삭제 */
        removeItem: (name: string): void => {
            localStorage.removeItem(name);
        },
    };
}
